import { Alert, Cell, FormControl, Grid, Heading, HFlow, Link, Text, Tooltip } from 'bold-ui'
import {
  CheckboxField,
  CnsField,
  CpfField,
  DateField,
  SexoSelectField,
  simNaoEnumToBoolean,
  SimNaoRadioGroupField,
  TextField,
} from 'components/form'
import { DeclaracaoObitoField } from 'components/form/field/DeclaracaoObitoField'
import { EstadoCivilSelectField } from 'components/form/field/select/EstadoCivilSelectField'
import { EtniaSelectField } from 'components/form/field/select/EtniaSelectField'
import { FuncaoSocialSelectField } from 'components/form/field/select/FuncaoSocialSelectField'
import { IdentidadeGeneroSelectField } from 'components/form/field/select/IdentidadeGeneroSelectField'
import { JustificativaNaoPossuiCpfSelectField } from 'components/form/field/select/JustificativaNaoPossuiCpfSelectField'
import { OrientacaoSexualSelectField } from 'components/form/field/select/OrientacaoSexualSelectField'
import { RacaCorSelectField } from 'components/form/field/select/RacaCorSelectField'
import { TipoSanguineoSelectField } from 'components/form/field/select/TipoSanguineoSelectField'
import { InfoIcon } from 'components/InfoIcon'
import { TourTarget } from 'components/tour/components/TourTarget'
import { useFlags } from 'config/useFlagsContext'
import { css } from 'emotion'
import { FiltroDocumentosCidadaoInput, RacaCorDbEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { toDate } from 'util/date/formatDate'
import { onlyNumbers } from 'util/mask'
import { MetaPath } from 'util/metaPath'
import { ErrorObject } from 'util/validation'
import { MSG_DADO_RECEBIDO_CADSUS } from 'view/cidadao/constants-cidadao'

import { CidadaoTourIdEnum } from '../tour-cidadao'
import { CidadaoAldeadoFormModel, DadosPessoaisFormModel } from '../types/model-cidadao'
import { DocumentosAssociados } from './DocumentosAssociados'
import { NacionalidadeFieldGroup } from './NacionalidadeFieldGroup'

interface DadosPessoaisFormSectionProps {
  meta: MetaPath<DadosPessoaisFormModel>
  metaIndigena: MetaPath<CidadaoAldeadoFormModel>
  values: DadosPessoaisFormModel
  valuesIndigena: CidadaoAldeadoFormModel
  serverTime: Date
  isCidadaoAldeado: boolean
  hasCadastrarEditarAldeadoPermission: boolean
  errors: ErrorObject<DadosPessoaisFormModel>
  isNovoCadastro: boolean
  onPesquisarCidadaos: (filter: FiltroDocumentosCidadaoInput) => void
  onRollbackCpfCns: (event: React.MouseEvent) => void
}

type DocumentosCidadao = FiltroDocumentosCidadaoInput

export const DadosPessoaisFormSection = (props: DadosPessoaisFormSectionProps) => {
  const {
    meta,
    metaIndigena,
    values,
    valuesIndigena,
    serverTime,
    isCidadaoAldeado,
    hasCadastrarEditarAldeadoPermission,
    errors,
    isNovoCadastro,
    onPesquisarCidadaos,
    onRollbackCpfCns,
  } = props

  const isIndigena = values?.racaCor?.value === RacaCorDbEnum.INDIGENA

  const desconheceMae = values?.desconheceNomeMae
  const desconhecePai = values?.desconheceNomePai

  const desejaInformarIdentidadeGenero = simNaoEnumToBoolean[values?.desejaInformarIdentidadeGenero]
  const desejaInformarOrientacaoSexual = simNaoEnumToBoolean[values?.desejaInformarOrientacaoSexual]

  const dataMinimaReconhecimento = values?.dataNascimento ? toDate(values.dataNascimento) : undefined

  const [lastSearchedDocs, setLastSearchedDocs] = useState<DocumentosCidadao>({})

  const { GESTAO_DOCUMENTO_CIDADAO_ENABLED } = useFlags()

  const handlePesquisarCidadaoRepetido = () => {
    if (isNovoCadastro) {
      const newDocs: FiltroDocumentosCidadaoInput = {
        cpfSet: values?.cpf ? [onlyNumbers(values?.cpf)] : undefined,
        cnsSet: values?.cns ? [onlyNumbers(values?.cns)] : undefined,
        nisSet: values?.nisPisPasep ? [onlyNumbers(values?.nisPisPasep)] : undefined,
        dnvSet: values?.numeroDnv ? [onlyNumbers(values?.numeroDnv)] : undefined,
      }

      const hasChanged = JSON.stringify(newDocs) !== JSON.stringify(lastSearchedDocs)
      const hasValuesToSearch = Object.values(newDocs).some((value) => value)

      if (hasChanged) {
        setLastSearchedDocs(newDocs)
      }

      if (hasChanged && hasValuesToSearch) {
        onPesquisarCidadaos(newDocs)
      }
    }
  }

  const stNaoPossuiCpf = values?.stNaoPossuiCpf
  const isCpfCadsus = values?.isCpfCadsus
  const isRegistroCadsus = values?.stRegistroCadsus

  return (
    <Grid>
      <Cell size={12}>
        <TourTarget id={CidadaoTourIdEnum.FORM_DADOS_PESSOAIS}>
          <Grid>
            <Cell size={12}>
              <Heading
                level={2}
                style={css`
                  margin-top: 1.5rem;
                `}
              >
                Dados Pessoais
              </Heading>
            </Cell>

            {errors?.initialCpf && (
              <Cell size={12}>
                <Alert type='danger' inline={true}>
                  {errors?.initialCpf}{' '}
                  <Link href='#' onClick={onRollbackCpfCns} style={{ fontWeight: 'bold', color: 'inherit' }}>
                    Desfazer
                  </Link>
                </Alert>
              </Cell>
            )}

            <Cell size={3}>
              <TourTarget id={CidadaoTourIdEnum.FORM_CPF}>
                <Tooltip text={isCpfCadsus ? MSG_DADO_RECEBIDO_CADSUS : ''}>
                  <CpfField
                    name={meta.cpf}
                    label='CPF'
                    required={!stNaoPossuiCpf}
                    disabled={isCpfCadsus || stNaoPossuiCpf}
                    onBlur={handlePesquisarCidadaoRepetido}
                  />
                </Tooltip>
              </TourTarget>
            </Cell>
            <Cell size={3}>
              <FormControl label={<span>&nbsp;</span>}>
                <CheckboxField
                  name={meta.stNaoPossuiCpf}
                  label='Cidadão não possui CPF'
                  style={css`
                    margin-top: '0.25rem';
                  `}
                  disabled={isCpfCadsus}
                />
              </FormControl>
            </Cell>
            {stNaoPossuiCpf && (
              <Cell size={3}>
                <JustificativaNaoPossuiCpfSelectField
                  name={meta.justificativaNaoPossuiCpf}
                  label='Justificativa para não possuir CPF'
                  required={stNaoPossuiCpf}
                />
              </Cell>
            )}
            <Cell size={stNaoPossuiCpf ? 3 : 6} />
            <Cell size={3}>
              <TextField
                name={meta.nisPisPasep}
                parse={onlyNumbers}
                maxLength={11}
                minLength={11}
                label='N° NIS (PIS/PASEP)'
                onBlur={handlePesquisarCidadaoRepetido}
              />
            </Cell>
            <Cell size={3}>
              <Tooltip text={isRegistroCadsus ? MSG_DADO_RECEBIDO_CADSUS : ''}>
                <CnsField
                  name={meta.cns}
                  label='CNS'
                  disabled={isRegistroCadsus}
                  onBlur={handlePesquisarCidadaoRepetido}
                />
              </Tooltip>
            </Cell>
            <Cell size={3}>
              <TextField
                name={meta.numeroDnv}
                parse={onlyNumbers}
                minLength={11}
                maxLength={15}
                label={
                  <HFlow hSpacing={0.5} alignItems='center'>
                    <Text>DNV</Text>
                    <InfoIcon icon='infoCircleFilled' text='Declaração de Nascido Vivo.' />
                  </HFlow>
                }
                onBlur={handlePesquisarCidadaoRepetido}
              />
            </Cell>
            <Cell size={3}>
              {hasCadastrarEditarAldeadoPermission && (
                <CheckboxField
                  name={meta.isCidadaoAldeado}
                  label='Cidadão é aldeado'
                  style={css`
                    margin-top: 1.75rem;
                  `}
                />
              )}
            </Cell>
          </Grid>
        </TourTarget>
      </Cell>
      <Cell size={6}>
        <TextField name={meta.nome} label='Nome completo' required />
      </Cell>
      <Cell size={6}>
        <TextField
          name={meta.nomeSocial}
          label={
            <HFlow hSpacing={0.5} alignItems='center'>
              <Text>Nome social</Text>
              <InfoIcon
                icon='infoCircleFilled'
                text='O nome social do cidadão cadastrado pelo PEC não será enviado ao CADSUS. Atualmente, só é possível incluir o nome social no CADSUS a partir do cadastro na Receita Federal.'
              />
            </HFlow>
          }
        />
      </Cell>
      {isCidadaoAldeado && (
        <Fragment>
          <Cell size={6}>
            <TextField name={metaIndigena.nomeTradicional} label='Nome tradicional' maxLength={70} />
          </Cell>
          <Cell size={6} />
        </Fragment>
      )}
      <Cell size={3}>
        <DateField name={meta.dataNascimento} maxDate={serverTime} label='Data de nascimento' required />
      </Cell>
      {isCidadaoAldeado && (
        <Fragment>
          <Cell size={3}>
            <DateField
              name={metaIndigena.dtReconhecimento}
              maxDate={serverTime}
              minDate={dataMinimaReconhecimento}
              label='Data de reconhecimento'
              required
            />
          </Cell>
          <Cell size={6} />
        </Fragment>
      )}
      <Cell size={3}>
        <SexoSelectField name={meta.sexo} label='Sexo' required />
      </Cell>
      <Cell size={3}>
        <EstadoCivilSelectField name={meta.estadoCivil} label='Estado civil' />
      </Cell>
      <Cell size={3}>
        <TipoSanguineoSelectField name={meta.tipoSanguineoEnum} label='Tipo sanguíneo' />
      </Cell>
      <Cell size={6}>
        <RacaCorSelectField name={meta.racaCor} label='Raça/Cor' required />
      </Cell>
      <Cell size={6}>
        <EtniaSelectField name={meta.etnia} label='Etnia' required={isIndigena} disabled={!isIndigena} />
      </Cell>
      {isCidadaoAldeado && (
        <Fragment>
          <Cell size={6}>
            <TextField name={metaIndigena.responsavelLegal} label='Responsável legal' maxLength={70} />
          </Cell>
          <Cell size={6}>
            <FuncaoSocialSelectField name={metaIndigena.funcaoSocial} label='Função social' />
          </Cell>
        </Fragment>
      )}
      <Cell size={6}>
        <TextField name={meta.nomeMae} label='Nome da mãe' required={!desconheceMae} disabled={desconheceMae} />
      </Cell>
      <Cell size={6}>
        <CheckboxField
          name={meta.desconheceNomeMae}
          label='Desconhece essa informação'
          style={css`
            margin-top: 1.75rem;
          `}
        />
      </Cell>
      <Cell size={6}>
        <TextField name={meta.nomePai} label='Nome do pai' required={!desconhecePai} disabled={desconhecePai} />
      </Cell>
      <Cell size={6}>
        <CheckboxField
          name={meta.desconheceNomePai}
          label='Desconhece essa informação'
          style={css`
            margin-top: 1.75rem;
          `}
        />
      </Cell>
      <Cell size={12}>
        <NacionalidadeFieldGroup
          meta={meta.nacionalidadeCidadao}
          maxDate={serverTime}
          values={values?.nacionalidadeCidadao}
          metaIndigena={metaIndigena}
          valuesIndigena={valuesIndigena}
          isCidadaoAldeado={isCidadaoAldeado}
          dataNascimento={values?.dataNascimento}
        />
      </Cell>
      <Cell size={3}>
        <SimNaoRadioGroupField
          label='Deseja informar identidade de gênero?'
          name={meta.desejaInformarIdentidadeGenero}
          required
        />
      </Cell>
      <Cell size={3}>
        <IdentidadeGeneroSelectField
          name={meta.identidadeGenero}
          label='Identidade de gênero'
          required={desejaInformarIdentidadeGenero}
          disabled={!desejaInformarIdentidadeGenero}
        />
      </Cell>
      <Cell size={3}>
        <SimNaoRadioGroupField
          label='Deseja informar orientação sexual?'
          name={meta.desejaInformarOrientacaoSexual}
          required
        />
      </Cell>
      <Cell size={3}>
        <OrientacaoSexualSelectField
          name={meta.orientacaoSexual}
          label='Orientação Sexual'
          required={desejaInformarOrientacaoSexual}
          disabled={!desejaInformarOrientacaoSexual}
        />
      </Cell>
      <Cell size={12}>
        <CheckboxField
          name={meta.faleceu}
          label='Cidadão faleceu'
          style={css`
            margin-top: 0.5rem;
          `}
        />
      </Cell>
      {values?.faleceu && (
        <Fragment>
          <Cell size={3}>
            <DateField name={meta.dataObito} maxDate={serverTime} label='Data de óbito' required />
          </Cell>
          <Cell size={3}>
            <DeclaracaoObitoField
              name={meta.numeroDeclaracaoObito}
              label='Número da declaração de óbito'
              style={css`
                margin-top: 0.1rem;
              `}
            />
          </Cell>
          <Cell size={6} />
        </Fragment>
      )}
      {GESTAO_DOCUMENTO_CIDADAO_ENABLED && values?.documentosAssociados?.length > 0 && (
        <Cell size={12}>
          <DocumentosAssociados name={meta.documentosAssociados} documentosAssociados={values.documentosAssociados} />
        </Cell>
      )}
    </Grid>
  )
}
